import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  MenuItem,
  Popper,
  Grid,
} from "@mui/material";
import { CheckCircleRounded } from "@mui/icons-material";
import useFetchUserGroup from "../../hooks/useFetchUserGroup";

interface AssignGroupProps {
  selectedGroupIds: number[];
  onGroupChange: (groupIds: number[]) => void;
  disabled?: boolean;
  label?: string;
  placeholder?: string;
  viewMode?: boolean;
  assignedGroups?: any[];
}

const AssignGroup: React.FC<AssignGroupProps> = ({
  selectedGroupIds,
  onGroupChange,
  disabled = false,
  label = "Assign Groups",
  placeholder = "Select...",
  viewMode = false,
  assignedGroups = [],
}) => {
  const [selectedIds, setSelectedIds] = useState<number[]>(selectedGroupIds);

  // Fetch group data for autocomplete
  const [userGroupData] = useFetchUserGroup({
    setIsLoading: () => {},
  });

  useEffect(() => {
    setSelectedIds(selectedGroupIds);
  }, [selectedGroupIds]);

  const handleChange = (_event: any, newValue: any[]) => {
    const newIds = newValue.map((group: any) => group.id);
    setSelectedIds(newIds);
    onGroupChange(newIds);
  };

  const renderGroupsPopper = (props: any) => (
    <Popper {...props} style={{ width: "100%" }} placement="bottom-start" />
  );

  // View mode rendering
  if (viewMode) {
    return (
      <Box className="text-box-card-white" sx={{ mt: 2 }}>
        <Box className="text-box-header">
          <h3>{label}</h3>
        </Box>
        <Grid container rowSpacing={1.5} columnSpacing={2.5}>
          <Grid item xs={12}>
            <label className="label-text">Groups</label>
            <Box sx={{ mt: 1 }}>
              {assignedGroups && assignedGroups.length > 0 ? (
                <div className="tags-tooltip-parent">
                  {assignedGroups.map((group: any, index: number) => (
                    <Box className="chip" key={group.id || index}>
                      <Typography variant="body2" fontWeight="medium">
                        {userGroupData.find(
                          (userGroup: { id: any }) =>
                            userGroup.id === group.user_group_id
                        )?.name ||
                          group.name ||
                          `Group ${group.user_group_id || group.id}`}
                      </Typography>
                    </Box>
                  ))}
                </div>
              ) : (
                <Box sx={{ color: "text.secondary", fontStyle: "italic" }}>
                  No groups assigned
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <>
      <div className="form-group non-flex">
        <div className="label-text">{label}</div>
        <div className="form-input">
          <Autocomplete
            id="assign-groups-autocomplete"
            PopperComponent={renderGroupsPopper}
            fullWidth={true}
            className="form-control-autocomplete"
            multiple
            options={userGroupData || []}
            getOptionLabel={(option: any) => option?.name || ""}
            disableCloseOnSelect
            disabled={disabled}
            onChange={handleChange}
            renderInput={(params) => (
              <>
                <div className="autocomplete-chips-direction">
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder={placeholder}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </div>
              </>
            )}
            renderTags={(_value, getTagProps) => {
              return (
                <div className="align-from-left">
                  {userGroupData
                    ?.filter((option: any) => selectedIds.includes(option.id))
                    ?.map((option: any, index: number) => (
                      <Box
                        {...getTagProps({ index })}
                        key={index}
                        className="chip"
                      >
                        <Typography variant="body2" fontWeight="medium">
                          {option?.name}
                        </Typography>
                      </Box>
                    ))}
                </div>
              );
            }}
            renderOption={(props, option: any, { selected }) => (
              <MenuItem
                {...props}
                key={`${option?.id}-${option?.name}`}
                value={option?.name}
                sx={{ justifyContent: "space-between" }}
              >
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    {option?.name}
                  </Typography>
                </Box>
                {selected ? <CheckCircleRounded color="info" /> : null}
              </MenuItem>
            )}
            value={
              selectedGroupIds
                .map((id) =>
                  (userGroupData || []).find(
                    (option: { id: number }) => option.id === id
                  )
                )
                .filter((option) => option !== undefined) || null
            }
            isOptionEqualToValue={(option: any, value: any) =>
              option.id === value.id
            }
          />
        </div>
      </div>
    </>
  );
};

export default AssignGroup;
